import { Incident, IncidentStatus, IncidentType } from "./types";
import { formatElapsed, formatWhen } from "./time";
import { ComplianceClock, buildCompliance } from "./compliance";

const STATUS_LABEL: Record<IncidentStatus, { en: string; fr: string }> = {
  pending_review: { en: "Awaiting staff review", fr: "En attente de révision du personnel" },
  awaiting_staff_review: { en: "Awaiting staff review", fr: "En attente de révision du personnel" },
  open: { en: "Open", fr: "Ouvert" },
  in_documentation: { en: "In documentation", fr: "Documentation en cours" },
  false_alert_pending_confirm: { en: "Dismissal pending confirmation", fr: "Rejet en attente de confirmation" },
  submitted_locked: { en: "Finalized and locked", fr: "Finalisé et verrouillé" },
  closed: { en: "Closed", fr: "Fermé" }
};

const TYPE_LABEL: Record<IncidentType, { en: string; fr: string }> = {
  fall: { en: "Fall", fr: "Chute" },
  unsafe_interaction: {
    en: "Unsafe interaction (responsive behaviour)",
    fr: "Interaction dangereuse (comportements réactifs)"
  },
  responsive_behaviour: { en: "Responsive behaviour", fr: "Comportement réactif" },
  wandering_zone: { en: "Wandering or exit-seeking", fr: "Errance ou sortie de zone" },
  other: { en: "Other alert", fr: "Autre alerte" }
};

export function pickLocale(locale: string | undefined, text: { en: string; fr: string }): string {
  return locale === "fr" ? text.fr : text.en;
}

export function typeLabel(type: IncidentType, locale?: string): string {
  return pickLocale(locale, TYPE_LABEL[type]);
}

/**
 * Display-only wording for abuse-detection copy.
 * Stored text, incident type keys, and model ids are left unchanged.
 */
export function displayCopy<T extends string | undefined | null>(text: T): T {
  if (!text) return text;
  return text
    .replace(/Abuse Detection/g, "Responsive Behaviour detection")
    .replace(/abuse detection/g, "responsive behaviour detection")
    .replace(/Détection d'abus/g, "Détection des comportements réactifs")
    .replace(/détection d'abus/g, "détection des comportements réactifs")
    .replace(/abus possible/g, "comportements réactifs")
    .replace(/Possible abuse/g, "Responsive behaviour")
    .replace(/possible abuse/g, "responsive behaviour") as T;
}

export function statusLabel(status: IncidentStatus, locale?: string): string {
  return pickLocale(locale, STATUS_LABEL[status]);
}

/** An unreviewed alert has no documentation form and no compliance countdown. */
export function isAwaitingStaffReview(incident: Incident): boolean {
  if (incident.confirmed_at) return false;
  if (incident.status === "false_alert_pending_confirm") return false;
  if (incident.status === "submitted_locked" || incident.status === "closed") return false;
  return (
    incident.status === "pending_review" ||
    incident.status === "awaiting_staff_review" ||
    incident.status === "open" ||
    incident.status === "in_documentation"
  );
}

export function displayStatus(incident: Incident): IncidentStatus {
  if (isAwaitingStaffReview(incident)) return "awaiting_staff_review";
  return incident.status;
}

/** The pre-filled form exists only after a person confirms the alert. */
export function documentationUnlocked(incident: Incident): boolean {
  if (!incident.confirmed_at) return false;
  if (incident.status === "false_alert_pending_confirm") return false;
  if (incident.false_alert && !incident.confirmed_at) return false;
  return true;
}

export function isRecordLocked(incident: Incident): boolean {
  if (incident.locked_at) return true;
  return (
    incident.status === "submitted_locked" ||
    incident.status === "closed" ||
    incident.status === "false_alert_pending_confirm"
  );
}

export type BadgeTone = "neutral" | "warning" | "error" | "success" | "info";

export interface CountdownBadge {
  tone: BadgeTone;
  label: string;
}

function badgeLabel(clocks: ComplianceClock[], locale?: string): CountdownBadge {
  const open = clocks.filter((clock) => clock.state !== "done");
  const fr = locale === "fr";
  if (open.some((clock) => clock.state === "due_now")) {
    return { tone: "error", label: fr ? "À faire maintenant" : "Due now" };
  }
  if (open.some((clock) => clock.state === "overdue")) {
    return { tone: "error", label: fr ? "En retard" : "Overdue" };
  }
  if (open.some((clock) => clock.state === "needed")) {
    return { tone: "warning", label: fr ? "Requis avant de finaliser" : "Needed to finalize" };
  }
  const timed = open.filter((clock) => clock.due_at).sort((a, b) => (a.due_at || "").localeCompare(b.due_at || ""));
  if (timed[0]?.due_at) {
    const when = formatWhen(timed[0].due_at, fr ? "fr-CA" : "en-CA");
    return { tone: "info", label: fr ? `Échéance ${when}` : `Due ${when}` };
  }
  return { tone: "success", label: fr ? "Aucune échéance ouverte" : "No open deadline" };
}

/**
 * Deadline chip for the incidents list.
 * An unreviewed row already shows "Awaiting staff review" as its status, so it does not get a second chip.
 */
export function listDeadlineBadge(incident: Incident, now: Date, locale?: string): CountdownBadge | null {
  if (isAwaitingStaffReview(incident)) return null;
  return countdownBadge(incident, now, locale);
}

/** List badge. Legal clocks do not run before confirmation. */
export function countdownBadge(incident: Incident, now: Date, locale?: string): CountdownBadge {
  const fr = locale === "fr";
  if (isAwaitingStaffReview(incident)) {
    return { tone: "info", label: fr ? "En attente de révision du personnel" : "Awaiting staff review" };
  }
  if (incident.status === "false_alert_pending_confirm" && incident.false_alert) {
    const due = new Date(incident.false_alert.dismissed_at).getTime() + 60 * 60000;
    if (now.getTime() >= due) {
      return { tone: "error", label: fr ? "Confirmation du superviseur en retard" : "Supervisor confirmation overdue" };
    }
    const mins = Math.max(1, Math.ceil((due - now.getTime()) / 60000));
    return {
      tone: "warning",
      label: fr ? `Confirmation du superviseur : ${mins} min` : `Supervisor confirmation: ${mins} min`
    };
  }
  return badgeLabel(buildCompliance(incident, now).clocks, locale);
}

export function elapsedSinceAlert(incident: Incident, now: Date, locale?: string): string {
  const elapsed = formatElapsed(new Date(incident.alert_at), now);
  if (locale === "fr") return `Temps depuis l'alerte : ${elapsed}`;
  return `Time since the alert: ${elapsed}`;
}
